// ═══════════════════════════════════════════════════════════════
// PilotMutationPanel.js — Cockpit de migration WordPress (Phase 1, lecture)
// ═══════════════════════════════════════════════════════════════
// Affiche : site connecté + stats d'inventaire + progression de mutation
// (E0→E3) + table filtrable des items (wp_source_item). Bouton « Rafraîchir
// l'inventaire » qui relance l'action `inventory` du connecteur.
// API : services/pilotMutationApi.js (GET overview / GET items / POST refresh).
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { Database, RefreshCw, FileText, File as FileIcon, Image as ImageIcon, Layers, ExternalLink, Search, AlertTriangle, Cpu, Send } from 'lucide-react';
import { pilotMutationApi } from '../../services/pilotMutationApi';
import IntegrateurWorkspace from './IntegrateurWorkspace';

const ACCENT = '#0ea5e9'; // cyan « mutation »

const STATUS_META = {
  discovered: { label: 'Découvert', color: '#64748b' },
  imported:   { label: 'Importé',   color: '#0ea5e9' },
  migrated:   { label: 'Migré',     color: '#10b981' },
  skipped:    { label: 'Ignoré',    color: '#a1a1aa' },
  error:      { label: 'Erreur',    color: '#ef4444' },
};
const TYPE_META = {
  post:       { label: 'Articles', single: 'Article', icon: FileText },
  page:       { label: 'Pages',    single: 'Page',    icon: FileIcon },
  attachment: { label: 'Médias',   single: 'Média',   icon: ImageIcon },
};
const PHASES = [
  { key: 'E0', label: 'Inventaire' },
  { key: 'E1', label: 'Import' },
  { key: 'E2', label: 'Redirects' },
  { key: 'E3', label: 'Bascule' },
];

const card = {
  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 12, padding: '14px 16px',
};

function StatCard({ icon: Icon, value, label, sub, color }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 130 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={15} color={color || ACCENT} />
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #6a6a85)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
      <div style={{ maxWidth: 420 }}>{children}</div>
    </div>
  );
}

export default function PilotMutationPanel({ projectId, canWrite = true }) {
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [q, setQ] = useState('');
  const [showIntegrateur, setShowIntegrateur] = useState(false);
  const [republishingId, setRepublishingId] = useState(null);
  const [republishingAll, setRepublishingAll] = useState(false);

  const loadOverview = useCallback(async () => {
    try { setOverview(await pilotMutationApi.overview(projectId)); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [projectId]);

  const loadItems = useCallback(async () => {
    try {
      const res = await pilotMutationApi.items(projectId, { type: filterType, status: filterStatus, q, limit: 100 });
      setItems(res.items || []); setItemsTotal(res.total || 0);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [projectId, filterType, filterStatus, q]);

  useEffect(() => { (async () => { setLoading(true); await loadOverview(); await loadItems(); setLoading(false); })(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { if (!loading) loadItems(); /* eslint-disable-next-line */ }, [filterType, filterStatus, q]);

  const onRefresh = async () => {
    setRefreshing(true); setError(null);
    try { await pilotMutationApi.refresh(projectId); await loadOverview(); await loadItems(); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setRefreshing(false); }
  };

  // Tour A : republie un lot d'items importés vers le site cible.
  const onRepublish = async (ids, all = false) => {
    if (!ids || !ids.length) return;
    if (all) setRepublishingAll(true); else setRepublishingId(ids[0]);
    setError(null);
    try {
      const res = await pilotMutationApi.republish(projectId, ids);
      if (res && res.ok === false) setError(res.error || 'Republication échouée.');
      await loadOverview(); await loadItems();
    } catch (e) {
      const d = e?.response?.data;
      setError((d && (d.message || d.error)) || e.message);
    } finally { setRepublishingId(null); setRepublishingAll(false); }
  };

  if (loading) return <Centered>Chargement du cockpit…</Centered>;
  if (overview && overview.connected === false) {
    return <Centered>Aucun site WordPress connecté à ce projet.<br />Active le connecteur <b>« WordPress (source) »</b> (menu <b>+</b>) pour piloter la mutation.</Centered>;
  }

  const s = overview?.stats || {};
  const byType = s.by_type || {};
  const byStatus = s.by_status || {};
  const progress = overview?.progress || { migrated: 0, total: 0, percent: 0 };
  const host = (() => { try { return new URL(overview?.site?.base_url).host; } catch { return overview?.site?.base_url || ''; } })();
  const importedLoaded = items.filter(i => i.status === 'imported');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '20px 24px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: `${ACCENT}1a`, display: 'grid', placeItems: 'center' }}>
            <Database size={19} color={ACCENT} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Pilot Mutation</div>
            <a href={overview?.site?.base_url} target="_blank" rel="noreferrer"
               style={{ fontSize: 12, color: ACCENT, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {host} <ExternalLink size={11} />
            </a>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowIntegrateur(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 8,
                     border: 'none', background: ACCENT, color: '#fff', fontSize: 13, fontWeight: 700,
                     cursor: 'pointer', boxShadow: `0 2px 12px ${ACCENT}55` }}>
            <Cpu size={14} /> Ouvrir l'Intégrateur
          </button>
          {canWrite && (
            <button onClick={onRefresh} disabled={refreshing}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 8,
                       border: `1px solid ${ACCENT}`, background: refreshing ? 'var(--card-bg)' : `${ACCENT}14`,
                       color: ACCENT, fontSize: 13, fontWeight: 600, cursor: refreshing ? 'wait' : 'pointer' }}>
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              {refreshing ? 'Inventaire en cours…' : "Rafraîchir l'inventaire"}
            </button>
          )}
          {canWrite && importedLoaded.length > 0 && (
            <button onClick={() => onRepublish(importedLoaded.map(i => i.id), true)} disabled={republishingAll}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 8,
                       border: 'none', background: republishingAll ? '#10b98199' : '#10b981', color: '#fff', fontSize: 13, fontWeight: 700,
                       cursor: republishingAll ? 'wait' : 'pointer', boxShadow: '0 2px 12px #10b98155' }}>
              <Send size={14} style={{ animation: republishingAll ? 'spin 1s linear infinite' : 'none' }} />
              {republishingAll ? 'Republication…' : `Republier importés (${importedLoaded.length})`}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ ...card, borderColor: '#ef444455', background: '#ef44440d', color: '#ef4444', fontSize: 13, marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <StatCard icon={FileText}  value={byType.post || 0}       label="Articles" />
        <StatCard icon={FileIcon}  value={byType.page || 0}       label="Pages" />
        <StatCard icon={ImageIcon} value={byType.attachment || 0} label="Médias" />
        <StatCard icon={Layers}    value={s.elementor || 0}       label="Elementor"
                  color={s.elementor ? '#f59e0b' : '#10b981'}
                  sub={s.elementor ? 'à traiter (vague 2)' : 'aucun — migration directe'} />
      </div>

      {/* ── Progression mutation ── */}
      <div style={{ ...card, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Progression de la mutation</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{progress.migrated} / {progress.total} migrés · {progress.percent}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 5, background: 'var(--card-border)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ width: `${progress.percent}%`, height: '100%', background: `linear-gradient(90deg, ${ACCENT}, #10b981)`, transition: 'width .4s' }} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {PHASES.map((p, i) => {
            const done = i === 0; // E0 inventaire fait dès qu'il y a des items
            return (
              <div key={p.key} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, padding: '5px 4px', borderRadius: 6,
                background: done ? `${ACCENT}14` : 'transparent', border: `1px solid ${done ? ACCENT + '44' : 'var(--card-border)'}`,
                color: done ? ACCENT : 'var(--text-muted, #6a6a85)', fontWeight: done ? 600 : 400 }}>
                <div>{p.key}</div><div>{p.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Filtres ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted, #6a6a85)' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un titre / slug…"
            style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selStyle}>
          <option value="">Tous types</option>
          <option value="post">Articles</option>
          <option value="page">Pages</option>
          <option value="attachment">Médias</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* ── Table items ── */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '9px 14px', borderBottom: '1px solid var(--card-border)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, color: 'var(--text-muted, #6a6a85)' }}>
          <span style={{ width: 70, flexShrink: 0 }}>Type</span>
          <span style={{ flex: 1, minWidth: 0 }}>Titre</span>
          <span style={{ width: 90, flexShrink: 0, textAlign: 'right' }}>Statut</span>
          <span style={{ width: 118, flexShrink: 0, textAlign: 'right' }}>Action</span>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 480px)', minHeight: 160, overflow: 'auto' }}>
          {items.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Aucun item pour ces filtres.</div>
          )}
          {items.map(it => {
            const tm = TYPE_META[it.wp_type] || { single: it.wp_type, icon: FileText };
            const sm = STATUS_META[it.status] || { label: it.status, color: '#64748b' };
            const TIcon = tm.icon;
            return (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid var(--card-border)', fontSize: 13 }}>
                <span style={{ width: 70, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)', fontSize: 11.5 }}>
                  <TIcon size={13} /> {tm.single}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>
                  {it.old_url
                    ? <a href={it.old_url} target="_blank" rel="noreferrer" style={{ color: 'var(--text)', textDecoration: 'none' }} title={it.old_url}>{it.title || it.slug || `#${it.wp_id}`}</a>
                    : (it.title || it.slug || `#${it.wp_id}`)}
                  {it.is_elementor && <span style={{ marginLeft: 6, fontSize: 10, color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: 4, padding: '0 4px' }}>Elementor</span>}
                </span>
                <span style={{ width: 90, flexShrink: 0, textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: sm.color, background: `${sm.color}1a`, borderRadius: 5, padding: '2px 8px' }}>{sm.label}</span>
                </span>
                <span style={{ width: 118, flexShrink: 0, textAlign: 'right' }}>
                  {canWrite && (it.status === 'imported' || it.status === 'error') && (
                    <button onClick={() => onRepublish([it.id])} disabled={republishingId === it.id || republishingAll}
                      title="Republier vers le site cible"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 6,
                               border: '1px solid #10b98155', background: '#10b9811a', color: '#10b981', fontSize: 11.5, fontWeight: 600,
                               cursor: (republishingId === it.id || republishingAll) ? 'wait' : 'pointer' }}>
                      <Send size={11} /> {republishingId === it.id ? '…' : 'Republier'}
                    </button>
                  )}
                  {it.status === 'migrated' && it.new_url && (
                    <a href={it.new_url} target="_blank" rel="noreferrer" title={it.new_url}
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: '#10b981', textDecoration: 'none' }}>
                      Voir <ExternalLink size={11} />
                    </a>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--text-muted, #6a6a85)', borderTop: '1px solid var(--card-border)' }}>
          {items.length} affichés{itemsTotal > items.length ? ` sur ${itemsTotal}` : ''}{byStatus ? '' : ''}
        </div>
      </div>

      {showIntegrateur && (
        <IntegrateurWorkspace
          projectId={projectId}
          canWrite={canWrite}
          onClose={() => setShowIntegrateur(false)}
          onImported={() => { loadOverview(); loadItems(); }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

const selStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--card-border)',
  background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)', fontSize: 13, cursor: 'pointer',
};
